/**
 * Deploy-confidence footer — answers the post-push question
 * "did production actually pick up the latest backend?" in one
 * glance, without curl.
 *
 * Reads `/api/health` once on mount. Bottom-right corner, faint type,
 * non-intrusive. Hidden on the survey-take route so a real respondent
 * never sees version metadata.
 */
import React from "react";
import axios from "axios";

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function VersionFooter() {
  const [health, setHealth] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/health`,
          { withCredentials: false },
        );
        if (alive) setHealth(data);
      } catch (_) { /* footer is best-effort */ }
    })();
    return () => { alive = false; };
  }, []);

  // Hide on survey-take (token-gated respondent path) — a real
  // respondent should never see infra metadata.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/survey/")) {
    return null;
  }

  if (!health) return null;

  const label = [
    `v${health.version}`,
    health.instrument_version,
    health.started_at ? `deployed ${formatRelative(health.started_at)}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div
      data-testid="version-footer"
      title={`Server started: ${health.started_at || "(unknown)"}`}
      style={{
        position: "fixed",
        bottom: 6,
        right: 10,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 10,
        letterSpacing: "0.04em",
        color: "#9A9A9A",
        background: "rgba(251, 250, 247, 0.85)",
        padding: "2px 8px",
        borderRadius: 2,
        pointerEvents: "auto",
        userSelect: "text",
        zIndex: 30,
      }}
    >
      {label}
    </div>
  );
}
